import styled from '@emotion/styled'
import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react'
import { AnalyticsContext } from '../../../../context/analyticsContext'
import { MEDIA_BORDER_RADIUS_VAR } from '../../../../context/CustomizationContextProvider'
import {
    FullScreenVideoContext,
    StorefrontPlaylist,
} from '../../../../context/fullScreenVideoContext'
import { VideoContext } from '../../../../context/viddyVideoContext'
import { useInView } from '../../../../hooks/useInView'
import { usePrefersReducedMotion } from '../../../../hooks/usePrefersReducedMotion'
import {
    inScrollingLayout,
    pageBleed,
    pageBleedTrackStyles,
} from '../../../../style/scrollingLayout'
import { Constants } from '../../../../style/styleConstants'
import { hoverFill } from '../../../../style/interactiveStates'
import { CarouselChevron } from '../../../icons/Icons'
import { SlideContent } from './CarouselSlide'

const AUTO_ADVANCE_INTERVAL_MS = 5000
const CARD_GAP = 10
/** How much of a card has to be inside the track for the preview to visit it. */
const VISIBLE_CARD_RATIO = 0.5

/** The width a slide has at the top of the phone carousel's range. */
const CARD_WIDTH = Math.round(Constants.CONTENT_BLOCK_DESKTOP_WIDTH * 0.68)

// Spans the page like the track inside it, so the arrows sit at the page
// edges over the cut-off card rather than inside the content column, where
// they would land on top of a whole card's play button.
const RowWrapper = styled.div({
    position: 'relative',
    ...inScrollingLayout(pageBleed),
})

/**
 * Cards sit still in a row that scrolls sideways once it overflows, so a
 * row that fits shows every video at once and a longer one shows the next
 * card cut off at the page edge, which is the cue that there is more.
 */
const Track = styled.div({
    display: 'flex',
    gap: CARD_GAP,
    overflowX: 'auto',
    // `overflow-x: auto` alone computes the other axis to `auto` too.
    overflowY: 'hidden',
    scrollSnapType: 'x proximity',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    // Too few cards to fill the track sit in the middle of it; `safe` keeps
    // the first card reachable once there are enough to overflow. Combined
    // with the bleed styles under one `inScrollingLayout` so the
    // container-query key isn't overwritten.
    ...inScrollingLayout({
        ...pageBleedTrackStyles,
        justifyContent: 'safe center',
    }),
})

const Card = styled.div({
    flex: '0 0 auto',
    width: CARD_WIDTH,
    scrollSnapAlign: 'start',
    cursor: 'pointer',
    borderRadius: `var(${MEDIA_BORDER_RADIUS_VAR})`,
    overflow: 'hidden',
})

/**
 * The product card's arrow rather than the phone carousel's: it sits over
 * the cut-off card, whose own play disc can fall anywhere under it as the
 * viewport changes, and two discs with a triangle in them read as one.
 */
const NavButton = styled.button<{ side: 'left' | 'right' }>(({ side }) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    [side]: 8,
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    padding: 0,
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    background: '#ffffff',
    color: '#1a1a1a',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.1)',
    ...hoverFill('#ffffff', '#1a1a1a'),
    transition: 'background-color 0.15s ease-out, color 0.15s ease-out',
}))

/**
 * Which way the track can still scroll. Both false when every card fits,
 * which is when the arrows have nothing to do and stay out of the way.
 */
function readOverflow(track: HTMLDivElement) {
    const maxScroll = track.scrollWidth - track.clientWidth
    return {
        left: track.scrollLeft > 1,
        right: track.scrollLeft < maxScroll - 1,
    }
}

/**
 * Wide-layout video carousel: a centred row of equal-height cards that never
 * move on their own. One card at a time plays its muted preview, the way the
 * centred slide does on a phone; the preview moves to the next card on a
 * timer, and hovering a card hands the preview to it.
 */
export const DesktopVideoRow: React.FC<{ playlist: StorefrontPlaylist }> = ({
    playlist,
}) => {
    const { videos } = playlist
    const reducedMotion = usePrefersReducedMotion()
    const wrapperRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const isInView = useInView(wrapperRef, { threshold: 0.1 })
    const [playingIdx, setPlayingIdx] = useState(0)
    const [overflow, setOverflow] = useState({ left: false, right: false })
    // Which cards are inside the track right now. The preview only moves
    // between these: a card scrolled out of the track would otherwise play
    // where nobody can see it, and scrolling to it would move the row.
    const visibleRef = useRef<Set<number>>(new Set())

    const { setCurrentIdx, setCurrentPlaylist } = useContext(
        FullScreenVideoContext,
    )
    const { setIsMuted, setVideoTapped, videoTapped } = useContext(VideoContext)
    const { Analytics } = useContext(AnalyticsContext)
    const videoTappedRef = useRef(videoTapped)
    videoTappedRef.current = videoTapped

    useEffect(() => {
        const track = trackRef.current
        if (!track) return
        const update = () => setOverflow(readOverflow(track))
        track.addEventListener('scroll', update, { passive: true })
        // Observing delivers a first measurement, so this also reads the
        // initial overflow. Re-run when the card count changes: that grows
        // `scrollWidth` without resizing the track's own box.
        const resize = new ResizeObserver(update)
        resize.observe(track)
        return () => {
            track.removeEventListener('scroll', update)
            resize.disconnect()
        }
    }, [videos.length])

    useEffect(() => {
        const track = trackRef.current
        if (!track) return
        const visible = visibleRef.current
        const cards = Array.from(track.children)
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    const idx = cards.indexOf(entry.target)
                    // `isIntersecting` is true for any overlap, and the
                    // observer also fires when that flips, so it would count
                    // a card as soon as a sliver of it enters.
                    if (entry.intersectionRatio >= VISIBLE_CARD_RATIO) {
                        visible.add(idx)
                    } else {
                        visible.delete(idx)
                    }
                }
            },
            { root: track, threshold: VISIBLE_CARD_RATIO },
        )
        cards.forEach((card) => observer.observe(card))
        return () => {
            observer.disconnect()
            visible.clear()
        }
    }, [videos.length])

    const advance = useCallback(() => {
        setPlayingIdx((current) => {
            const visible = visibleRef.current
            if (visible.size === 0) return current
            for (let step = 1; step <= videos.length; step++) {
                const next = (current + step) % videos.length
                if (visible.has(next)) return next
            }
            return current
        })
    }, [videos.length])

    // Restarts whenever a hover hands the preview to a card, so the card
    // gets a full interval before the preview moves on.
    const [timerEpoch, setTimerEpoch] = useState(0)
    useEffect(() => {
        if (reducedMotion || !isInView) return
        const timer = setInterval(advance, AUTO_ADVANCE_INTERVAL_MS)
        return () => clearInterval(timer)
    }, [advance, reducedMotion, isInView, timerEpoch])

    const onCardHover = (idx: number) => {
        if (idx === playingIdx) return
        setPlayingIdx(idx)
        setTimerEpoch((epoch) => epoch + 1)
    }

    const onSlideClick = useCallback(
        (idx: number) => {
            setCurrentPlaylist(playlist)
            setCurrentIdx(idx)
            if (!videoTappedRef.current) {
                setVideoTapped(true)
                setIsMuted(false)
            }
            const v = videos[idx]
            Analytics.TRACK_CAROUSEL_EXPAND(playlist, v.name, v.videoHash)
        },
        [
            playlist,
            videos,
            setCurrentPlaylist,
            setCurrentIdx,
            setIsMuted,
            setVideoTapped,
            Analytics,
        ],
    )

    const scrollByCard = (direction: -1 | 1) => {
        trackRef.current?.scrollBy({
            left: direction * (CARD_WIDTH + CARD_GAP),
            behavior: reducedMotion ? 'auto' : 'smooth',
        })
    }

    return (
        <RowWrapper ref={wrapperRef}>
            {overflow.left && (
                <NavButton
                    side="left"
                    onClick={() => scrollByCard(-1)}
                    aria-label="Previous"
                >
                    <CarouselChevron width={20} direction="left" />
                </NavButton>
            )}
            {overflow.right && (
                <NavButton
                    side="right"
                    onClick={() => scrollByCard(1)}
                    aria-label="Next"
                >
                    <CarouselChevron width={20} direction="right" />
                </NavButton>
            )}
            <Track ref={trackRef}>
                {videos.map((v, i) => (
                    <Card
                        key={`${v.videoHash}-${i}`}
                        onMouseEnter={() => onCardHover(i)}
                    >
                        <SlideContent
                            onSlideClick={onSlideClick}
                            slideWidth={CARD_WIDTH}
                            v={v}
                            idx={i}
                            isActive={
                                i === playingIdx && isInView && !reducedMotion
                            }
                            shouldPreload={
                                i === playingIdx ||
                                i === (playingIdx + 1) % videos.length
                            }
                            priority={i === 0}
                        />
                    </Card>
                ))}
            </Track>
        </RowWrapper>
    )
}
